/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, { useRef } from 'react';
import { Grid, Stack, Card, Tooltip, TextField, CircularProgress, Alert, Autocomplete } from '@mui/material';
import { Button, Typography, Box } from 'components/index';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
  type MRT_VisibilityState,
} from 'material-react-table';
import { Delete, Edit as EditIcon, Warning, ErrorOutline, CheckCircleOutline } from '@mui/icons-material';
import {
  CreateLineItemDTO,
  ReportGeneralInfo,
  ReportLineItem,
  ReportLineItemTypes,
  ReportStatusEnum,
  ReportLineItemNotations,
} from '../report/report.type';
import theme from '../../styles/theme';
import { Division, LineItemForPrediction, LineItemUnitEnum } from '../glossary/glossary.type';
import { usePrompt } from '../AppPrompt';
import { getTotalsRow, getDivisionColor, compareArrays, TOOLTIPS, tableRowTypes, tableRowsComparator } from './report.util';
import { toast } from 'react-toastify';
import LineItemsBulkdEditDialog from './LineItemBulkEditDialog';
import { ReportLineItemBulkEditFormValues } from './report.type';
import CreateLineItemDialog from './CreateLineItemDialog';
import { getReportColumns, RenderDescriptionCellWithDrag } from './ReportColumns';
import { REPORT_COLORS, HEIGHTS, TOTAL_ID, WIDTHS } from './constants';
import WhiteFab from '../common/WhiteFab';
import CompsAverages from './CompsAverages';
import { addOrUpdateNote } from '../../components/glossary/glossary.api';
import { joinDateAndUser } from 'common/utils/common.util';
import keys from 'common/react-query/data-keys';
import { useQueryClient } from 'react-query';
import { NoteDTO } from 'common/utils/util.type';
import ReportDivisionCustomToolbar from './ReportDivisionCustomToolbar';
import { Comp } from '../comp/comp.type';
import { useDragDropManager, useDrop } from 'react-dnd';

// Define a drag type
export const DRAG_TYPE = 'LINE_ITEM';

const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }];
const notationsList = [...Object.values(ReportLineItemNotations).map(notation => ({ value: notation, label: notation }))];

const noRowsOverlay = () => (
  <Box display={'flex'} alignItems={'center'} justifyContent={'center'} height={'100%'}>
    <Typography variant='button'></Typography>
  </Box>
);

interface ReportDivisionProps {
  reportId: string;
  handleDrop?: (draggedItem: any, targetDivision: Division | undefined) => void;
  reportStatus: string;
  reportSelectedComps: Comp[];
  division: Division | null;
  gridData: ReportLineItem[];
  divisions: Division[];
  updateLineItem: (updatedLineItem: ReportLineItem) => void;
  globalSelectedLineItemsRef: React.MutableRefObject<ReportLineItem[]>;
  onUpdateGlobalSelection?: (items: ReportLineItem[]) => void;
  setSelectedLineItem: (lineItem: ReportLineItem | null) => void;
  setLineItemEditDialogOpen: (isOpen: boolean) => void;
  setLineItemNoteDialogOpen: (isOpen: boolean) => void;
  handleOpenProjectDialog: (divisionId: string, divisionCode: string, divisionName: string) => void;
  reportGeneralInfo: ReportGeneralInfo;
  hardCostSubTotal?: number;
  totalProjectCost?: number;
  isLocked?: boolean;
  name?: string;
  saveLineItem: (lineItemData: CreateLineItemDTO, showNotification?: boolean) => Promise<void>;
  registerReturnComp?: (divisionId: string, addCompFunc: () => Promise<CreateLineItemDTO>) => void;
  showActions?: boolean;
  hideNotes?: boolean;
  openAutoAssignDialog?: (lineItemsToAutoAssign: LineItemForPrediction[]) => void;
  predictingDivisions?: boolean;
  updateReportWithModifiedLineItems: (modifiedLineItems: ReportLineItem[]) => void;
  showUnassignedAlert?: boolean;
  overheadAndProfitPercentage?: number | null;
  costThreshold?: number | null;
}

export const ReportDivisionTable: React.FC<ReportDivisionProps> = ({
  reportId,
  handleDrop,
  reportStatus,
  reportSelectedComps,
  updateLineItem,
  division,
  gridData,
  divisions,
  setLineItemEditDialogOpen,
  globalSelectedLineItemsRef,
  onUpdateGlobalSelection,
  setSelectedLineItem,
  handleOpenProjectDialog,
  hardCostSubTotal,
  totalProjectCost,
  reportGeneralInfo,
  isLocked,
  name,
  saveLineItem,
  registerReturnComp,
  showActions = true,
  hideNotes = false,
  openAutoAssignDialog,
  predictingDivisions = false,
  updateReportWithModifiedLineItems,
  showUnassignedAlert,
  overheadAndProfitPercentage,
  costThreshold,
}) => {
  const { getConfirmation } = usePrompt();
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const [isLineItemDialogOpen, setIsLineItemDialogOpen] = React.useState(false);
  const [selectedLineItems, setSelectedLineItems] = React.useState<ReportLineItem[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<MRT_VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});
  const [divisionNote, setDivisionNote] = React.useState(division?.note?.content || '');
  const [isDivisionNoteEdited, setIsDivisionNoteEdited] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const editedInfo = React.useMemo(
    () =>
      division?.note?.updatedOn && division?.note?.updatedBy
        ? joinDateAndUser(division?.note?.updatedOn, division?.note?.updatedBy)
        : division?.note?.createdOn && division?.note?.createdBy
          ? joinDateAndUser(division?.note?.createdOn, division?.note?.createdBy)
          : '',
    [division],
  );

  const handleMoveLineItemsToDivision = (targetDivisionId: string) => {
    if (!targetDivisionId || !selectedLineItems.length) return;

    const updatedLineItems = selectedLineItems.map(item => ({
      ...item,
      division: divisions.find(div => div.id === targetDivisionId) ?? null,
      isEdited: true,
    }));

    updateReportWithModifiedLineItems(updatedLineItems);
    setSelectedLineItems([]);
  };

  const listRef = useRef<HTMLDivElement | null>(null);
  const divisionTableContainerRef = useRef<HTMLDivElement | null>(null);
  const compsContainerRef = useRef<HTMLDivElement | null>(null);

  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();
  const [isOver, setIsOver] = React.useState(false);

  React.useEffect(() => {
    let animationFrameId: number;
    let lastScrollTime = 0;
    const scrollInterval = 50; // Time interval between scrolls in milliseconds

    const handleOffsetChange = () => {
      const offset = monitor.getSourceClientOffset();
      if (offset) {
        const { y } = offset;

        // Get the viewport height
        const viewportHeight = window.innerHeight;

        // Define a custom threshold (e.g., 200px from the top or bottom)
        const threshold = 200;

        // Trigger scrolling when the drag item is within the threshold
        if (y > viewportHeight - threshold) {
          window.scrollBy({ top: 40, behavior: 'auto' }); // Scroll down
          lastScrollTime = performance.now();
        } else if (y < threshold) {
          window.scrollBy({ top: -40, behavior: 'auto' }); // Scroll up
          lastScrollTime = performance.now();
        }
      }

      // Request the next animation frame if the drag item is still within the threshold
      if (performance.now() - lastScrollTime < scrollInterval) {
        animationFrameId = requestAnimationFrame(handleOffsetChange);
      }
    };

    const unsubscribe = monitor.subscribeToOffsetChange(handleOffsetChange);

    return () => {
      unsubscribe();
      cancelAnimationFrame(animationFrameId);
    };
  }, [monitor]);

  const [, dropRef] = useDrop(() => ({
    accept: DRAG_TYPE,

    drop: (item: { divisionId: string; selectedRows?: ReportLineItem[]; fullRow?: ReportLineItem }) => {
      if (item.divisionId !== division?.id && handleDrop) {
        handleDrop(item, division || undefined);
      }
    },

    collect: monitor => {
      const isCurrentlyOver = monitor.isOver({ shallow: true });

      setIsOver(isCurrentlyOver);
      return {};
    },
  }));

  React.useEffect(() => {
    setDivisionNote(division?.note?.content || '');
    setIsDivisionNoteEdited(false);
  }, [division, isLocked]);

  const handleAutoAssignDivisions = () => {
    openAutoAssignDialog
      ? openAutoAssignDialog(
          gridData
            .filter(li => li.type == null || li.type == ReportLineItemTypes.NORMAL)
            .map(lineItem => {
              return {
                id: lineItem.id,
                description: lineItem.lineItem.description,
                revisedBudget: lineItem.totalBudget,
              };
            }),
        )
      : toast.error('Auto-Assign feature not available');
  };

  const handleNoteChange = React.useCallback((event: any) => {
    setDivisionNote(event.target.value);
    setIsDivisionNoteEdited(event.target.value !== (division?.note || ''));
  }, []);

  const handleNoteUpdate = React.useCallback(async () => {
    if (division?.id) {
      setIsLoading(true);
      const note: NoteDTO = {
        id: division?.note ? division.note.id : '',
        content: divisionNote,
      };
      try {
        const updatedNote = await addOrUpdateNote(division?.id, reportId, note);
        console.log('Note updated successfully:', updatedNote);
        setIsDivisionNoteEdited(false);
        queryClient.invalidateQueries([keys.divisions]);
        toast.success('Note updated successfully!');
      } catch (error) {
        toast.error('Error updating note');
      }
      setIsLoading(false);
    }
  }, [division, divisionNote, queryClient, reportId]);

  const onSelectionModelChange = React.useCallback(
    (newModel: MRT_RowSelectionState) => {
      const selectedIds = Object.keys(newModel).filter(id => newModel[id]);
      const selectedLineItems = gridData.filter(li => selectedIds.includes(li.id));
      setSelectedLineItems(selectedLineItems);
      onUpdateGlobalSelection?.(selectedLineItems);
    },
    [gridData],
  );

  const handleOpenLineItemDialog = React.useCallback(() => {
    setIsLineItemDialogOpen(true);
  }, []);

  const handleCloseLineItemDialog = React.useCallback(() => {
    setIsLineItemDialogOpen(false);
  }, []);

  const handleBulkDelete = React.useCallback(async () => {
    const selectedIds = selectedLineItems.map(li => li.id);
    if (selectedIds.length === 0) {
      toast.error('Please select at least one row!');
      return;
    }
    const confirmed = await getConfirmation({ description: 'Are you sure you want to delete these line items?' });
    if (!confirmed) return;
    const updatedLineItems = selectedLineItems.map(li => ({ ...li, isDeleted: true }));
    updateReportWithModifiedLineItems(updatedLineItems);
  }, [selectedLineItems]);

  const handleDeleteReportLineItem = React.useCallback(
    async (id: string) => {
      const confirmed = await getConfirmation({ description: 'Are you sure you want to delete this line item?' });
      if (!confirmed) return;
      const deletedLineItem = gridData.find(li => li.id === id);
      if (!deletedLineItem) return;
      updateReportWithModifiedLineItems([{ ...deletedLineItem, isDeleted: true }]);
    },
    [getConfirmation],
  );
  const handleBulkEditClick = React.useCallback(() => {
    if (selectedLineItems.length === 0) {
      toast.error('Please select at least one row!');
      return;
    }
    setBulkEditDialogOpen(true);
  }, [selectedLineItems]);

  const handleBulkEditSubmit = React.useCallback(
    (values: ReportLineItemBulkEditFormValues) => {
      if (selectedLineItems.length === 0) {
        toast.error('Failed to update. No line items found!');
        return;
      }
      const updatedLineItems = gridData.map(lineItem => {
        if (selectedLineItems.find(li => li.id === lineItem.id)) {
          return { ...lineItem, ...values, isEdited: true };
        }
        return lineItem;
      });
      updateReportWithModifiedLineItems(updatedLineItems);
      setBulkEditDialogOpen(false);
    },
    [selectedLineItems],
  );

  const renderActionsHeader = React.useCallback(
    () => (
      <Stack direction={'row'} spacing={1} sx={{ width: '100%', justifyContent: 'flex-end' }}>
        <Tooltip
          title={
            selectedLineItems.length === 0 || (isLocked && reportStatus === ReportStatusEnum.Completed)
              ? TOOLTIPS.statusCompleted
              : isLocked
                ? TOOLTIPS.unlockToEdit
                : ''
          }
          disableHoverListener={!isLocked}
          arrow={false}
        >
          <span>
            <WhiteFab
              aria-label='edit'
              icon={<EditIcon fontSize='small' />}
              textColor='primary'
              onClick={handleBulkEditClick}
              size='small'
              disabled={selectedLineItems.length === 0 || isLocked}
            />
          </span>
        </Tooltip>
        <Tooltip
          title={
            selectedLineItems.length === 0 || (isLocked && reportStatus === ReportStatusEnum.Completed)
              ? TOOLTIPS.statusCompleted
              : isLocked
                ? TOOLTIPS.unlockToEdit
                : ''
          }
          disableHoverListener={!isLocked}
          arrow={false}
        >
          <span>
            <WhiteFab
              aria-label='delete'
              icon={<Delete fontSize='small' />}
              textColor='error'
              onClick={handleBulkDelete}
              size='small'
              disabled={selectedLineItems.length === 0 || isLocked}
            />
          </span>
        </Tooltip>
      </Stack>
    ),
    [handleBulkEditClick, handleBulkDelete, selectedLineItems, isLocked],
  );

  const renderActionsCell = React.useCallback(
    (params: any) => {
      const isTotal = (params.row.type != null && params.row.type != ReportLineItemTypes.NORMAL) || params.row.id == TOTAL_ID;
      if (isTotal && division == null) return null;
      return (
        <Stack direction={'row'} spacing={1} px={1}>
          <Tooltip
            title={
              selectedLineItems.length > 0 || (isLocked && reportStatus === ReportStatusEnum.Completed)
                ? TOOLTIPS.statusCompleted
                : isLocked
                  ? TOOLTIPS.unlockToEdit
                  : 'Edit line item'
            }
            arrow={false}
          >
            <span>
              <WhiteFab
                aria-label='edit'
                icon={<EditIcon fontSize='small' />}
                textColor='primary'
                onClick={(e: any) => {
                  e.stopPropagation();
                  setSelectedLineItem(params.row);
                  setLineItemEditDialogOpen(true);
                }}
                size='small'
                disabled={selectedLineItems.length > 0 || isLocked}
              />
            </span>
          </Tooltip>
          {!isTotal && (
            <Tooltip
              title={
                selectedLineItems.length > 0 || (isLocked && reportStatus === ReportStatusEnum.Completed)
                  ? TOOLTIPS.statusCompleted
                  : isLocked
                    ? TOOLTIPS.unlockToEdit
                    : 'Delete line item'
              }
              arrow={false}
            >
              <span>
                <WhiteFab
                  aria-label='delete'
                  icon={<Delete fontSize='small' />}
                  textColor='error'
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleDeleteReportLineItem(params.row.id);
                  }}
                  size='small'
                  disabled={selectedLineItems.length > 0 || isLocked}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
      );
    },
    [handleDeleteReportLineItem, division, selectedLineItems, isLocked],
  );

  const getSelectedLineItems = React.useCallback(() => {
    return globalSelectedLineItemsRef?.current || [];
  }, [globalSelectedLineItemsRef]);

  const renderDescriptionCell = React.useCallback(
    (params: any) => RenderDescriptionCellWithDrag(params, getSelectedLineItems),
    [getSelectedLineItems],
  );

  const convertToMRTColumns = React.useCallback(
    (gridColumns: any[], totalsData: any): MRT_ColumnDef<any>[] => {
      return gridColumns.map(col => {
        const headerNode = col.renderHeader ? col.renderHeader({}) : col.headerName || col.field;

        let accessorKey: string | undefined;
        let accessorFn: ((row: any) => any) | undefined;

        if (col.valueGetter) {
          accessorFn = (row: any) => col.valueGetter(null, row);
        } else if (col.field) {
          accessorKey = col.field;
        }

        const mrtColumn: MRT_ColumnDef<any> = {
          id: col.field,
          accessorKey,
          accessorFn,
          header: headerNode,
          size: col.width || (col.flex ? 150 : col.minWidth || 80),
          minSize: col.minWidth || 50,
          maxSize: col.maxWidth || 300,
          grow: Boolean(col.flex),
          enableSorting: col.sortable !== false,
          enableColumnFilter: false,
          enableEditing:
            !isLocked && col.editable && ['totalBudget', 'originalBudget', 'quantity', 'unit', 'notations'].includes(col.field),
          muiTableHeadCellProps: {
            className: col.headerClassName,
            sx: {
              textAlign: col.type === 'number' ? 'right' : 'left',
              margin: 0,
              height: '52px',
              minHeight: '52px',
              display: 'flex',
              alignItems: 'center',
              padding: '8px 16px',
              '& .Mui-TableHeadCell-Content': {
                alignItems: 'center',
              },
              ...(col.field === 'actions' && { justifyContent: 'flex-end' }),
            },
          },
          muiTableBodyCellProps: ({ row }) => ({
            className: col.cellClassName,
            sx: {
              textAlign: col.type === 'number' ? 'right' : 'left',
              borderLeft: 'none',
              borderRight: 'none',
              height: '52px !important',
              minHeight: '52px !important',
              maxHeight: '52px !important',
              padding: '8px 16px',
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: (() => {
                if (row.original?.type === ReportLineItemTypes.Adjustment && col.field !== 'description') {
                  return REPORT_COLORS.ADJUSTMENT_COLOR;
                }
                if (row.original?.type === ReportLineItemTypes.CompPricing) {
                  return REPORT_COLORS.COMP_PRICING_COLOR;
                }
                return 'inherit';
              })(),
              fontWeight: row.original?.type === ReportLineItemTypes.CompPricing ? 'bold' : 'normal',
            },
          }),
        };

        if (col.renderCell) {
          mrtColumn.Cell = ({ cell, row }) => {
            const params = {
              value: cell.getValue(),
              row: row.original,
              field: col.field,
              id: row.id,
              api: null,
              hasFocus: false,
              tabIndex: -1,
            };
            return col.renderCell(params);
          };
        } else if (col.valueFormatter) {
          mrtColumn.Cell = ({ cell, row }) => {
            const value = cell.getValue();
            const formattedValue = col.valueFormatter(value, row.original, col, null);
            return <span>{formattedValue}</span>;
          };
        }

        // Add footer rendering for totals row
        // Important: for columns with valueGetter, compute footer value using valueGetter
        const getFooterValue = () => (col.valueGetter ? col.valueGetter(null, totalsData) : (totalsData as any)[col.field]);

        if (col.renderCell) {
          mrtColumn.Footer = () => {
            const params = {
              value: getFooterValue(),
              row: totalsData,
              field: col.field,
              id: totalsData.id,
              api: null,
              hasFocus: false,
              tabIndex: -1,
            };
            return col.renderCell(params);
          };
        } else if (col.valueFormatter) {
          mrtColumn.Footer = () => {
            const value = getFooterValue();
            const formattedValue = col.valueFormatter(value, totalsData, col, null);
            return <span>{formattedValue}</span>;
          };
        } else if (col.field && (totalsData as any)[col.field] !== undefined) {
          mrtColumn.Footer = () => <span>{getFooterValue()}</span>;
        }

        if (col.field === 'unit' && col.editable) {
          mrtColumn.Edit = ({ cell, table }) => {
            const handleChange = (unit: string) => {
              const updatedData = { ...cell.row.original };
              updatedData.unit = unit;
              processRowUpdate(updatedData, cell.row.original);
              table.setEditingCell(null);
            };

            return (
              <select
                value={cell.getValue<string>() || ''}
                onChange={e => handleChange(e.target.value)}
                style={{ width: '100%', padding: '8px' }}
              >
                {unitsList.map(unit => (
                  <option key={unit.value} value={unit.value}>
                    {unit.label}
                  </option>
                ))}
              </select>
            );
          };
        } else if (col.field === 'notations' && col.editable) {
          mrtColumn.Edit = ({ cell, table }) => {
            const handleChange = (notation: string) => {
              const updatedData = { ...cell.row.original };
              updatedData.notation = notation;
              processRowUpdate(updatedData, cell.row.original);
              table.setEditingCell(null);
            };

            return (
              <Autocomplete
                freeSolo
                options={notationsList.map(item => item.value)}
                value={cell.getValue<string>() || ''}
                onChange={(_, newVal) => handleChange(newVal || '')}
                renderInput={params => <TextField {...params} variant='standard' style={{ width: '100%' }} />}
              />
            );
          };
        }

        return mrtColumn;
      });
    },
    [isLocked],
  );

  const rows = React.useMemo(() => {
    const normalRows = gridData.filter(row => row.type == null || tableRowTypes.includes(row.type as ReportLineItemTypes)) || [];
    const sortedNormalRows = normalRows.sort(tableRowsComparator);
    return sortedNormalRows;
  }, [gridData]);

  const gridColumns = React.useMemo(
    () =>
      getReportColumns(renderActionsHeader, renderActionsCell, false, showActions, false, renderDescriptionCell).map(col => {
        if (isLocked) {
          col.editable = false;
        }
        if (col.field === 'description') {
          return {
            ...col,
            minWidth: 400,
          };
        }
        return col;
      }),
    [renderActionsHeader, renderActionsCell, showActions, isLocked, globalSelectedLineItemsRef.current],
  );

  // Prepare the totals row data for the footer (moved up before columns)
  const totalsRowData = React.useMemo(() => {
    return getTotalsRow(
      'TOTAL',
      rows,
      reportGeneralInfo.grossBuildingSF || 1,
      division ? division.code : name === 'ALLOWANCES' ? 'allowances' : 'no-division',
      hardCostSubTotal,
      totalProjectCost,
      gridData.filter(row => row.type === ReportLineItemTypes.DivisionTotal)[0] || undefined,
    );
  }, [rows, reportGeneralInfo.grossBuildingSF, division, name, hardCostSubTotal, totalProjectCost, gridData]);

  const columns = React.useMemo(() => convertToMRTColumns(gridColumns, totalsRowData), [gridColumns, convertToMRTColumns, totalsRowData]);

  const processRowUpdate = React.useCallback(
    (updatedRow: any, originalRow: any) => {
      const same = compareArrays(updatedRow, originalRow);
      if (!same) {
        if (
          updatedRow.type === ReportLineItemTypes.DivisionTotal &&
          (division?.code === '1.00' || division?.code === '17.00') &&
          updatedRow.cdccCompPerUnit !== originalRow.cdccCompPerUnit
        ) {
          const compPricingLineItem = gridData.find(
            row => row.type === ReportLineItemTypes.CompPricing && row.lineItem.description.startsWith('Comparable Pricing'),
          );

          if (compPricingLineItem && hardCostSubTotal) {
            const newCdccCompPerUnit = Math.round(updatedRow.cdccCompPerUnit * hardCostSubTotal * 100) / 10000;
            const updatedCompPricingLineItem = {
              ...compPricingLineItem,
              cdccCompPerUnit: newCdccCompPerUnit,
              isEdited: true,
            };
            updateLineItem(updatedCompPricingLineItem);
          }
        }
        updateLineItem(updatedRow);
      }
      return { ...updatedRow };
    },
    [division?.code, gridData, hardCostSubTotal],
  );

  // Determine heading text for card
  const headingText = React.useMemo(() => {
    const defaultHeading = 'NO DIVISION ASSIGNED (AND UNDEFINED CHANGE ORDERS)';

    if (name) return name;
    if (division) return `${division.code} ${division.name.toLocaleUpperCase()}`;

    return showUnassignedAlert ? (
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Tooltip title='There are unassigned line items in the cost analysis' arrow>
          <Warning color='warning' fontSize='small' />
        </Tooltip>
        {defaultHeading}
      </span>
    ) : (
      defaultHeading
    );
  }, [name, division, showUnassignedAlert]);

  const shouldShowBanner =
    overheadAndProfitPercentage != null && costThreshold != null && (division?.code === '1.00' || division?.code === '17.00');

  // Row spacing will be handled via CSS classes in MRT

  return (
    <React.Fragment key={division?.id ?? 'No-Division'}>
      <Grid size={{ xs: 12 }} ref={dropRef}>
        <Card
          elevation={1}
          sx={{
            padding: '0px 10px 10px 10px',
            border: isOver ? '2px solid #0283a0' : '2px solid transparent',
            transition: 'border 0.2s ease-in-out',
          }}
        >
          <Box ref={listRef}>
            {showActions && (
              <ReportDivisionCustomToolbar
                headingText={headingText}
                division={division}
                predictingDivisions={predictingDivisions}
                isLocked={isLocked}
                reportStatus={reportStatus}
                handleOpenProjectDialog={handleOpenProjectDialog}
                handleOpenLineItemDialog={handleOpenLineItemDialog}
                handleAutoAssignDivisions={handleAutoAssignDivisions}
                selectedLineItems={selectedLineItems}
                divisions={divisions}
                onMoveLineItemsToDivision={handleMoveLineItemsToDivision}
              />
            )}
            <Stack direction={'row'} sx={{ overflowX: 'hidden', overflowY: 'visible' }}>
              <Stack direction={'row'} spacing={0} padding={0} sx={{ width: '100%' }}>
                <Grid container sx={{ width: division ? `calc(100% - ${WIDTHS.COMPS_TABLE_WIDTH}px)` : '100%', flex: '1 1 auto' }}>
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                    ref={divisionTableContainerRef}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        overflow: 'visible',
                        height: 'auto',
                        maxHeight: '800px',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '100%',
                      }}
                    >
                      <MaterialReactTable
                        table={useMaterialReactTable({
                          columns,
                          data: rows,
                          getRowId: row => row.id,
                          enableRowSelection: !isLocked,
                          enableMultiRowSelection: true,
                          enablePagination: false,
                          enableTopToolbar: false,
                          enableBottomToolbar: false,
                          enableColumnFilters: false,
                          enableSorting: false,
                          enableColumnActions: false,
                          enableDensityToggle: false,
                          enableFullScreenToggle: false,
                          enableGlobalFilter: false,
                          enableColumnResizing: true,
                          enableColumnPinning: true,
                          enablePinning: true,
                          columnResizeMode: 'onEnd',
                          enableStickyHeader: true,
                          enableTableFooter: true,
                          enableStickyFooter: true,
                          layoutMode: 'grid',
                          editDisplayMode: 'cell',
                          enableEditing: !isLocked,
                          muiEditTextFieldProps: ({ cell, column, row }) => ({
                            onBlur: event => {
                              // Save on blur (clicking elsewhere)
                              const value = event.target.value;
                              const columnId = column.id;

                              // Map column IDs to actual data properties
                              const updatedRow = { ...row.original };

                              if (columnId === 'totalBudget' || columnId === 'originalBudget') {
                                updatedRow.totalBudget = parseFloat(value) || 0;
                              } else if (columnId === 'quantity') {
                                updatedRow.quantity = parseFloat(value) || 0;
                              } else if (columnId === 'unit') {
                                updatedRow.unit = value;
                              } else if (columnId === 'cdccCompPerUnit') {
                                updatedRow.cdccCompPerUnit = parseFloat(value) || 0;
                              } else if (columnId === 'notations') {
                                updatedRow.notation = value;
                              } else {
                                // Fallback to direct property assignment
                                (updatedRow as any)[columnId] = value;
                              }

                              processRowUpdate(updatedRow, row.original);
                            },
                            onKeyDown: event => {
                              // Save on Enter key
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                const value = (event.target as HTMLInputElement).value;
                                const columnId = column.id;

                                // Map column IDs to actual data properties
                                const updatedRow = { ...row.original };

                                if (columnId === 'totalBudget' || columnId === 'originalBudget') {
                                  updatedRow.totalBudget = parseFloat(value) || 0;
                                } else if (columnId === 'quantity') {
                                  updatedRow.quantity = parseFloat(value) || 0;
                                } else if (columnId === 'unit') {
                                  updatedRow.unit = value;
                                } else if (columnId === 'cdccCompPerUnit') {
                                  updatedRow.cdccCompPerUnit = parseFloat(value) || 0;
                                } else if (columnId === 'notations') {
                                  updatedRow.notation = value;
                                } else {
                                  // Fallback to direct property assignment
                                  (updatedRow as any)[columnId] = value;
                                }

                                processRowUpdate(updatedRow, row.original);
                              }
                            },
                          }),
                          state: {
                            columnVisibility: columnVisibilityModel,
                            rowSelection,
                          },
                          initialState: {
                            columnPinning: {
                              left: ['mrt-row-select', 'description'],
                              right: ['actions'],
                            },
                          },
                          onColumnVisibilityChange: setColumnVisibilityModel,
                          onRowSelectionChange: updater => {
                            const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
                            setRowSelection(newSelection);
                            onSelectionModelChange(newSelection);
                          },
                          muiTableContainerProps: {
                            sx: {
                              ...(division
                                ? {
                                    height: '500px',
                                    minHeight: '500px',
                                    maxHeight: '500px',
                                    overflow: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }
                                : {
                                    height: 'auto',
                                    maxHeight: '800px',
                                    overflow: 'auto',
                                  }),
                              margin: 0,
                              padding: 0,
                              width: '100%',
                              position: 'relative',
                            },
                          },
                          muiTableProps: {
                            sx: {
                              ...divisionTableStyles,
                              borderBottom: 'none',
                              border: 'none',
                              boxShadow: 'none',
                              margin: 0,
                              tableLayout: 'auto',
                              minWidth: '100%',
                              ...(division && {
                                height: '100%',
                                display: 'grid',
                                gridTemplateRows: 'auto 1fr auto',
                              }),
                              // Remove default pinned overlay color applied by MRT/MUI
                              '& .MuiTableCell-root[data-pinned="true"]::before': {
                                backgroundColor: 'transparent !important',
                              },
                              '& .MuiTableCell-root[data-index="0"]': {
                                position: 'sticky !important',
                                left: '0px !important',
                                zIndex: '2 !important',
                              },
                              '& .MuiTableRow-root.Mui-selected': {
                                borderTop: 'none',
                                borderBottom: 'none',
                              },
                              '& .MuiTableRow-root.Mui-selected .MuiTableCell-root': {
                                borderTop: 'none !important',
                                borderBottom: 'none !important',
                              },
                              // Ensure pinned headers (like Description) use division color
                              '& .MuiTableHead-root th[data-pinned="true"]': {
                                backgroundColor: `${getDivisionColor(division?.code ?? 'no-division')} !important`,
                              },
                              '& .MuiTableHead-root th[data-pinned="true"] .Mui-TableHeadCell-Content': {
                                backgroundColor: 'transparent !important',
                              },
                              // Remove all body cell borders
                              '& .MuiTableBody-root td': {
                                borderBottom: 'none',
                                borderTop: 'none',
                              },
                              '& .MuiTableBody-root td[data-pinned="true"]::before': {
                                borderBottom: 'none',
                                borderTop: 'none',
                              },
                              '& .MuiTableHead-root .MuiTableRow-root': {
                                height: '52px !important',
                                minHeight: '52px !important',
                                maxHeight: '52px !important',
                              },
                              '& .MuiTableHead-root .MuiTableCell-root': {
                                height: '52px !important',
                                minHeight: '52px !important',
                                maxHeight: '52px !important',
                                display: 'flex',
                                alignItems: 'center',
                              },
                              '& .MuiTableHead-root': {
                                margin: 0,
                              },
                              '& .projectHeader': {
                                backgroundColor: getDivisionColor(division?.code ?? 'no-division'),
                                borderTop: '0.1px solid',
                                borderColor: REPORT_COLORS.BORDER,
                              },
                              '& .MuiTableCell-root': {
                                borderLeft: 'none',
                                borderRight: 'none',
                                minHeight: '52px !important',
                                height: '52px !important',
                                maxHeight: '52px !important',
                                padding: '8px 16px',
                                //verticalAlign: 'middle',
                              },
                              '& .MuiTableBody-root .MuiTableCell-root': {
                                minHeight: '52px !important',
                                height: '52px !important',
                                maxHeight: '52px !important',
                                //verticalAlign: 'middle',
                              },
                              '& .MuiTableRow-root': {
                                backgroundColor: 'white',
                                minHeight: '52px !important',
                                height: '52px !important',
                                maxHeight: '52px !important',
                              },
                              '& .MuiTableBody-root .MuiTableRow-root': {
                                minHeight: '52px !important',
                                height: '52px !important',
                                maxHeight: '52px !important',
                                flex: 'none',
                              },
                              ...(division && {
                                '& .MuiTableHead-root': {
                                  gridRow: '1',
                                  display: 'table-header-group',
                                },
                                '& .MuiTableBody-root': {
                                  gridRow: '2',
                                  overflow: 'auto',
                                  display: 'block',
                                  minHeight: 0,
                                },
                                '& .MuiTableFooter-root': {
                                  gridRow: '3',
                                  display: 'table-footer-group',
                                },
                              }),
                            },
                          },
                          muiTableHeadProps: {
                            sx: {
                              position: 'sticky',
                              top: 0,
                              zIndex: 1000,
                              padding: 0,
                              backgroundColor: 'white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              '& .MuiTableRow-root': {
                                height: '52px',
                                minHeight: '52px',
                              },
                            },
                          },
                          muiTableHeadCellProps: ({ column }) => {
                            const divisionColor = getDivisionColor(division?.code ?? 'no-division');

                            // Determine header cell background color based on column type
                            let headerBackgroundColor;
                            if (column?.id === 'actions') {
                              headerBackgroundColor = '#ffffff';
                            } else if (
                              ['cdccCompPerUnit', 'cdccCompTotal', 'deviation', 'deviationPercentage', 'notations'].includes(
                                column?.id || '',
                              )
                            ) {
                              // CDCC columns should have blue background
                              headerBackgroundColor = REPORT_COLORS.BLUE;
                            } else {
                              // Regular data columns use division color
                              headerBackgroundColor = divisionColor;
                            }

                            return {
                              sx: {
                                backgroundColor: headerBackgroundColor,
                                fontWeight: '500',
                                borderTop: '0.1px solid',
                                borderColor: REPORT_COLORS.BORDER,
                                height: '52px',
                                minHeight: '52px',
                                padding: '8px 16px',
                                borderLeft: 'none',
                                borderRight: 'none',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                position: 'sticky',
                                top: 0,
                                zIndex: 999,
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                textAlign: 'center',
                                '& .MuiBox-root': {
                                  whiteSpace: 'normal',
                                  wordWrap: 'break-word',
                                },
                                '&.projectHeader': {
                                  backgroundColor: headerBackgroundColor,
                                },
                                '&[data-pinned="true"]:not([role="columnheader"])::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  backgroundColor: headerBackgroundColor,
                                  zIndex: 0,
                                },
                                '&[data-pinned="true"][role="columnheader"]::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  backgroundColor: headerBackgroundColor,
                                  zIndex: 0,
                                },
                                '&[data-pinned="true"]': {
                                  backgroundColor: headerBackgroundColor,
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1001,
                                },
                                '&[data-pinned="true"]:not([role="columnheader"])': {
                                  zIndex: 1,
                                },
                              },
                            };
                          },
                          muiTableBodyCellProps: ({ row }) => {
                            const backgroundColor = row.original?.isEdited
                              ? REPORT_COLORS.UNSAVED_BACKGROUND_COLOR
                              : row.index % 2 === 0
                                ? '#ffffff'
                                : '#f9f9f9';

                            return {
                              sx: {
                                border: 'none',
                                '&[data-pinned="true"]:before': {
                                  backgroundColor: backgroundColor + ' !important',
                                },
                              },
                            };
                          },
                          muiTableBodyRowProps: ({ row }) => {
                            // Check if there are normal rows above this adjustment/comp-pricing row
                            const currentRowIndex = rows.findIndex(r => r.id === row.original.id);
                            const hasNormalRowsAbove =
                              currentRowIndex > 0 &&
                              rows.slice(0, currentRowIndex).some(r => r.type == null || r.type === ReportLineItemTypes.NORMAL);

                            // For comp-pricing rows, also check if there are adjustment rows above
                            const hasAdjustmentRowsAbove =
                              row.original?.type === ReportLineItemTypes.CompPricing &&
                              currentRowIndex > 0 &&
                              rows.slice(0, currentRowIndex).some(r => r.type === ReportLineItemTypes.Adjustment);

                            const backgroundColor = row.original?.isEdited
                              ? REPORT_COLORS.UNSAVED_BACKGROUND_COLOR
                              : row.index % 2 === 0
                                ? '#ffffff'
                                : '#f9f9f9';

                            return {
                              className: (() => {
                                const classes = [];
                                if (row.original?.isEdited) classes.push('edited-row');
                                if (row.original?.type === ReportLineItemTypes.Adjustment)
                                  classes.push('adjustment-row', 'extra-top-border-row');
                                if (row.original?.type === ReportLineItemTypes.CompPricing)
                                  classes.push('comp-pricing-row', 'extra-top-border-row');
                                return classes.join(' ');
                              })(),
                              sx: {
                                // Fixed height for all rows to prevent stretching
                                height: '52px !important',
                                minHeight: '52px !important',
                                maxHeight: '52px !important',
                                // Row background color (edited or zebra striping)
                                backgroundColor: backgroundColor + ' !important',
                                '& td[data-pinned="true"]::before': {
                                  backgroundColor: backgroundColor,
                                },
                                // Extra top spacing if:
                                // - Adjustment/comp-pricing row AND has normal rows above
                                // - OR comp-pricing row AND has adjustment rows above
                                ...((row.original?.type === ReportLineItemTypes.Adjustment ||
                                  row.original?.type === ReportLineItemTypes.CompPricing) &&
                                  (hasNormalRowsAbove || hasAdjustmentRowsAbove) && {
                                    marginTop: `${HEIGHTS.EXTRA_ROW_SPACING}px`,
                                  }),
                                ...(row.getIsSelected?.() && {
                                  borderTop: 'none',
                                  borderBottom: 'none',
                                }),
                              },
                            };
                          },
                          displayColumnDefOptions: {
                            'mrt-row-select': {
                              size: 50,
                              enableResizing: false,
                              enableSorting: false,
                              muiTableHeadCellProps: {
                                sx: {
                                  backgroundColor: getDivisionColor(division?.code ?? 'no-division'),
                                  '&[data-pinned="true"]': {
                                    backgroundColor: getDivisionColor(division?.code ?? 'no-division'),
                                  },
                                },
                              },
                            },
                          },

                          muiTableFooterProps: {
                            sx: {
                              ...divisionTotalsStyles,
                              backgroundColor: 'white',
                              borderTop: `0.1px solid ${REPORT_COLORS.BORDER}`,
                              zIndex: 1,
                              '& .MuiTableRow-root': {
                                height: '52px !important',
                                minHeight: '52px !important',
                                maxHeight: '52px !important',
                                fontWeight: 700,
                              },
                              '& .MuiTableCell-root': {
                                fontWeight: 700 + ' !important',
                                fontSize: '0.85em !important',
                                verticalAlign: 'middle',
                                lineHeight: '1.2',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                              },
                              ...(division && {
                                position: 'sticky',
                                bottom: 0,
                                gridRow: '3',
                              }),
                            },
                          },
                          muiTableFooterCellProps: ({ column }) => {
                            const divisionColor = getDivisionColor(division?.code ?? 'no-division');
                            const backgroundColor = totalsRowData?.isEdited ? REPORT_COLORS.UNSAVED_BACKGROUND_COLOR : divisionColor;

                            // Determine cell background color based on column type
                            let cellBackgroundColor;
                            if (column?.id === 'actions') {
                              cellBackgroundColor = '#ffffff';
                            } else if (
                              ['cdccCompPerUnit', 'cdccCompTotal', 'deviation', 'deviationPercentage', 'notations'].includes(
                                column?.id || '',
                              )
                            ) {
                              // CDCC columns should have blue background
                              cellBackgroundColor = totalsRowData?.isEdited ? REPORT_COLORS.UNSAVED_BACKGROUND_COLOR : REPORT_COLORS.BLUE;
                            } else {
                              // Regular data columns use division color
                              cellBackgroundColor = backgroundColor;
                            }

                            return {
                              className: totalsRowData?.isEdited ? 'edited-row projectTotal' : 'projectTotal',
                              sx: {
                                backgroundColor: cellBackgroundColor + ' !important',
                                fontWeight: 700,
                                fontSize: '0.85em',
                                borderLeft: 'none',
                                borderRight: 'none',
                                height: '52px !important',
                                minHeight: '52px !important',
                                maxHeight: '52px !important',
                                padding: '8px 16px',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                '&[data-pinned="true"]:before': {
                                  backgroundColor: cellBackgroundColor + ' !important',
                                },
                                // Ensure pinned cells (like description) use division color
                                '&[data-pinned="true"]': {
                                  backgroundColor: cellBackgroundColor + ' !important',
                                },
                              },
                            };
                          },
                          renderEmptyRowsFallback: () => noRowsOverlay(),
                        })}
                      />
                    </Box>
                  </Grid>
                </Grid>
                {division ? (
                  <Grid
                    sx={{
                      maxWidth: 'unset !important',
                      position: 'sticky',
                      right: 0,
                      zIndex: 900,
                      background: 'white',
                      borderLeft: `10px solid white`,
                      width: `${WIDTHS.COMPS_TABLE_WIDTH}px`,
                      flex: '0 0 auto',
                    }}
                  >
                    <CompsAverages
                      reportId={reportId}
                      reportGeneralInfo={reportGeneralInfo}
                      reportStatus={reportStatus}
                      reportSelectedComps={reportSelectedComps}
                      division={division}
                      saveLineItem={saveLineItem}
                      hardCost={hardCostSubTotal}
                      isLocked={isLocked}
                      registerReturnComp={registerReturnComp}
                      containerRef={compsContainerRef}
                    />
                  </Grid>
                ) : null}
              </Stack>
            </Stack>
            {shouldShowBanner && (
              <Alert
                sx={{ mt: 2 }}
                severity={overheadAndProfitPercentage > costThreshold ? 'error' : 'success'}
                icon={overheadAndProfitPercentage > costThreshold ? <ErrorOutline /> : <CheckCircleOutline />}
              >
                {`OH & P + General Conditions = ${overheadAndProfitPercentage.toFixed(2)}% of Report Budget`}
              </Alert>
            )}
            {!hideNotes && (
              <>
                <TextField
                  fullWidth
                  label='Notes'
                  multiline
                  rows={2}
                  margin='normal'
                  variant='outlined'
                  placeholder='Add a note...'
                  value={divisionNote}
                  onChange={e => {
                    const value = e.target.value;
                    if (value.trim() !== '' || value === '') {
                      handleNoteChange(e);
                    }
                  }}
                  disabled={isLocked}
                />

                <Box sx={{ mt: 0, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='subtitle2' fontSize='small'>
                    {editedInfo && <React.Fragment>Last Edited: {editedInfo}</React.Fragment>}
                  </Typography>
                  {isDivisionNoteEdited && (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={handleNoteUpdate}
                      startIcon={isLoading ? <CircularProgress size={20} color='inherit' /> : null}
                      disabled={isLocked}
                    >
                      Update Note
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Card>
      </Grid>
      <LineItemsBulkdEditDialog
        open={bulkEditDialogOpen}
        onClose={() => {
          setBulkEditDialogOpen(false);
        }}
        divisions={divisions}
        onSubmit={handleBulkEditSubmit}
      />
      <CreateLineItemDialog
        open={isLineItemDialogOpen}
        onClose={handleCloseLineItemDialog}
        currentDivision={division}
        divisions={divisions}
        saveLineItem={saveLineItem}
        reportGeneralInfo={reportGeneralInfo}
      />
    </React.Fragment>
  );
};

const divisionTableStyles = {
  borderBottom: 'none',
  backgroundColor: theme.palette.background.paper,
  '& .MuiDataGrid-columnHeaders': {
    fontWeight: 'bold',
    borderRadius: '0',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: REPORT_COLORS.WHITE,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1.2,
    height: 'auto',
    textOverflow: 'clip',
    maxHeight: 'unset !important',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'unset',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'unset',
  },
  '& .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: 'unset',
  },
  '&  .MuiDataGrid-row.edited-row': {
    backgroundColor: REPORT_COLORS.UNSAVED_BACKGROUND_COLOR,
  },
  '& .MuiTableRow-root.edited-row': {
    backgroundColor: REPORT_COLORS.UNSAVED_BACKGROUND_COLOR,
  },

  '& .compHeader': {
    backgroundColor: REPORT_COLORS.BLUE,
    borderTop: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
  },
  // DataGrid selectors (legacy)
  '& .MuiDataGrid-row.adjustment-row': {
    color: REPORT_COLORS.ADJUSTMENT_COLOR,
  },
  '& .MuiDataGrid-row.comp-pricing-row': {
    color: REPORT_COLORS.COMP_PRICING_COLOR,
    fontWeight: 'bold',
    borderTop: `0.1px solid ${REPORT_COLORS.BORDER}`,
    borderBottom: `0.1px solid ${REPORT_COLORS.BORDER} !important`,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '0.1px',
      backgroundColor: REPORT_COLORS.BORDER,
      zIndex: 1,
    },
    '& .MuiDataGrid-cell': {
      borderBottom: `0.1px solid ${REPORT_COLORS.BORDER} !important`,
    },
  },
  '& .MuiDataGrid-row.extra-top-border-row': {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: `-${HEIGHTS.EXTRA_ROW_SPACING}px`,
      left: 0,
      right: 0,
      height: '0.1px',
      backgroundColor: REPORT_COLORS.BORDER,
    },
  },
  // MRT selectors - add row colors to match original behavior
  '& .MuiTableRow-root.adjustment-row': {
    color: REPORT_COLORS.ADJUSTMENT_COLOR,
  },
  '& .MuiTableRow-root.comp-pricing-row': {
    color: REPORT_COLORS.COMP_PRICING_COLOR,
    fontWeight: 'bold',
    borderTop: `0.1px solid ${REPORT_COLORS.BORDER}`,
    borderBottom: `0.1px solid ${REPORT_COLORS.BORDER} !important`,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '0.1px',
      backgroundColor: REPORT_COLORS.BORDER,
      zIndex: 1,
    },
    '& .MuiTableCell-root': {
      borderBottom: `0.1px solid ${REPORT_COLORS.BORDER} !important`,
    },
  },
  '& .MuiTableRow-root.adjustment-row .MuiTableCell-root': {
    borderTop: `0.1px solid ${REPORT_COLORS.BORDER}`,
  },
  '& .MuiTableRow-root.extra-top-border-row': {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: `-${HEIGHTS.EXTRA_ROW_SPACING}px`,
      left: 0,
      right: 0,
      height: '0.1px',
      backgroundColor: REPORT_COLORS.BORDER,
    },
  },
  '.MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitleContainer':
    {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      paddingLeft: 1,
    },
  '& .MuiDataGrid-cellCheckbox': {
    paddingLeft: 1,
  },
};

const divisionTotalsStyles = {
  fontSize: '0.95em',
  '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeaders': {
    display: 'none',
    borderBottom: 'none',
  },
  '& .MuiDataGrid-cellContent': {
    whiteSpace: 'normal',
    lineHeight: 1.2,
    height: 'auto',
    textOverflow: 'clip',
    maxHeight: 'unset !important',
  },
  '& .compTotal': {
    backgroundColor: REPORT_COLORS.BLUE,
  },
};

export default React.memo(ReportDivisionTable);
